/*************************************************************************
 *
 *  Copyright 2020 Adobe
 *  All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/

import {
  isIOS,
  isCurrentDeviceMobile,
  isCurrentDeviceTablet
} from "../Utils/DeviceUtils";
import { isSafari10, isSafari, isFirefox } from "../Utils/UserAgentUtils";

const createElement = (elementName, parentElementRef) => {
  const element = document.createElement(elementName);
  const style = {
    height: "0px",
    left: "-10000px",
    position: "absolute",
    top: "-10000px",
    width: "0px"
  };
  Object.assign(element.style, style);
  parentElementRef.appendChild(element);
  return element;
};

const createEditableDiv = (text, parentElementRef) => {
  const editableDiv = createElement("div", parentElementRef);
  editableDiv.innerText = text;
  editableDiv.contentEditable = true;
  editableDiv.tabIndex = -1;

  return editableDiv;
};

const createTextArea = (text, parentElementRef) => {
  const textArea = createElement("textarea", parentElementRef);
  textArea.innerText = text;
  return textArea;
};

const createAndAddRangeWithTextArea = textArea => {
  const range = document.createRange();
  range.selectNodeContents(textArea);
  const selection = window.getSelection();
  selection.removeAllRanges();
  selection.addRange(range);
};

const selectText = (textArea, text) => {
  if (isIOS() || isSafari10()) {
    const {
      contentEditable: oldContentEditable,
      readOnly: oldReadOnly
    } = textArea;

    // need to set these properties for IOS Safari
    textArea.contenteditable = true;
    textArea.readonly = false;

    createAndAddRangeWithTextArea(textArea);
    if (isSafari10() && !isCurrentDeviceMobile() && !isCurrentDeviceTablet()) {
      textArea.setSelectionRange(0, text.length - 1);
    }

    // restoring the properties
    textArea.contentEditable = oldContentEditable;
    textArea.readOnly = oldReadOnly;
  } else {
    textArea.select();
  }
};

const fallbackCopyTextToClipboard = (text, parentElementRef) => {
  const textArea = createTextArea(text, parentElementRef);
  selectText(textArea, text);

  try {
    // Fallback: Copy successfull
    document.execCommand("Copy");
  } catch (err) {
    console.error("Fallback: Oops, unable to copy", err);
  }

  parentElementRef.removeChild(textArea);
};

const copyTextToClipboard = (text, parentElementRef = document.body) => {
  if (!window.navigator.clipboard) {
    fallbackCopyTextToClipboard(text, parentElementRef);
  } else {
    const textArea = createTextArea(text, parentElementRef);
    selectText(textArea, text);
    window.navigator.clipboard.writeText(text).then(
      () => {
        // Async: Copy successfull
      },
      err => {
        console.error("Async: Could not copy text: ", err);
        fallbackCopyTextToClipboard(text, parentElementRef);
      }
    );
    parentElementRef.removeChild(textArea);
  }
};

const manualPaste = parentElementRef =>
  new Promise(resolve => {
    const text = "out of view text"; // text is necessary in the div else the editable div won't come in focus
    const editableDiv = createEditableDiv(text, parentElementRef);
    editableDiv.onpaste = evt => {
      evt.stopPropagation();
      evt.preventDefault();

      // Get pasted data via clipboard API
      const clipboardData = evt.clipboardData || window.clipboardData;
      const pasteText = clipboardData.getData("Text");

      resolve(pasteText);
      parentElementRef.removeChild(editableDiv);
    };
    editableDiv.focus({
      preventScroll: true
    });
  });

/**
 * NOTE:
 * 1) Don't call preventDefault on the keyDown event when CMD+V/ CTRL+V is pressed as it triggers the paste event.
 * 2) This function changes focus to a new created div and listens paste event on it. The caller module will have
 *    take care of the refocussing of the previously focussed element.
 *
 * This function will return a promise which resolves to pasted text.
 * @param {Event} keyDownEvent the keyDown event which is emitted on pressing CMD + V / CTRL + V
 * @param {Boolean} shouldPreventDefault flag which tells whether should prevent the default behaviour of the keydownEvent
 * @param {HTMLElement} parentElementRef the element on which the editable div (which listens to paste event) will be created.
 */
const pasteTextFromClipboard = (
  keyDownEvent,
  shouldPreventDefault = true,
  parentElementRef = document.body
) => {
  if (isSafari()) {
    try {
      if (shouldPreventDefault) {
        keyDownEvent.preventDefault(); // since we are using api, we are preventing its default behaviour
      }
      return navigator.clipboard.readText();
    } catch (e) {
      return Promise.reject(e);
    }
  } else {
    return manualPaste(parentElementRef);
  }
};

const fallbackCopyTextWithFormattingToClipboard = (plainText, htmlText) => {
  const listener = e => {
    e.clipboardData.setData("text/html", htmlText);
    e.clipboardData.setData("text/plain", plainText);
    e.preventDefault();
  };
  document.addEventListener("copy", listener);
  document.execCommand("copy");
  document.removeEventListener("copy", listener);
};

const copyTextWithFormattingToClipboard = (text, divId) => {
  const element = document.getElementById(divId);
  element.contentEditable = true;
  element.readOnly = false;
  if (!window.navigator.clipboard || isFirefox()) {
    fallbackCopyTextWithFormattingToClipboard(text, element.outerHTML);
    return;
  }
  const textblob = new Blob([text], { type: "text/plain" });
  const htmlblob = new Blob([element.outerHTML], { type: "text/html" });
  const data = [
    new window.ClipboardItem({ "text/html": htmlblob, "text/plain": textblob })
  ];

  navigator.clipboard
    .write(data)
    .then(() => {
      // Success
    })
    .catch(e => {
      console.log("Copy formatted text with clipboard api failed: ", e);
      fallbackCopyTextWithFormattingToClipboard(text, element.outerHTML);
    });
};

export default {
  copyTextToClipboard,
  pasteTextFromClipboard,
  copyTextWithFormattingToClipboard
};
